'use client'

/**
 * global-error.tsx — handles errors thrown by the ROOT app/layout.tsx
 * itself (font loading, providers, html/body setup). This is the ONLY
 * boundary that catches errors at the root level, and it's required by
 * Next.js 14 — without it the runtime emits "missing required error
 * components, refreshing..." and the dev server gets stuck in a recovery
 * loop.
 *
 * Per Next.js docs:
 *  • MUST be a Client Component
 *  • MUST include its own <html> and <body> tags (it replaces the root
 *    layout entirely when active)
 *  • Should NOT depend on the root layout's providers/fonts (because
 *    those might be exactly what failed)
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  // Inline styles only — no Tailwind, no CSS imports — because the
  // root layout (which loads our globals.css) may have failed.
  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          padding: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
          background:
            'linear-gradient(160deg, #f7f5ef 0%, #f4f0e5 22%, #f0f5fc 45%, #f3f9f6 68%, #faf6ee 88%, #f7f5ef 100%)',
          color: '#0a0a0f',
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: '100%',
            margin: '0 24px',
            padding: '32px 28px',
            borderRadius: 20,
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(10, 10, 15, 0.08)',
            boxShadow: '0 22px 50px rgba(37, 99, 235, 0.07), 0 6px 16px rgba(10, 10, 15, 0.06)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'rgba(239, 68, 68, 0.1)',
              marginBottom: 20,
              fontSize: 28,
            }}
          >
            ⚠️
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 800,
              margin: '0 0 8px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Something went really wrong
          </h1>
          <p
            style={{
              fontSize: 14,
              color: 'rgba(10, 10, 15, 0.6)',
              lineHeight: 1.6,
              margin: '0 0 8px 0',
            }}
          >
            The application failed to load. This is usually caused by a stale dev cache —
            try refreshing, or if it persists, restart the dev server.
          </p>
          {error?.message && (
            <p
              style={{
                fontSize: 12,
                color: 'rgba(10, 10, 15, 0.5)',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                margin: '12px 0',
                padding: '8px 12px',
                background: 'rgba(10, 10, 15, 0.04)',
                borderRadius: 8,
                wordBreak: 'break-word',
              }}
            >
              {error.message}
            </p>
          )}
          {error?.digest && (
            <p
              style={{
                fontSize: 10,
                color: 'rgba(10, 10, 15, 0.4)',
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                margin: '0 0 20px 0',
              }}
            >
              ref: {error.digest}
            </p>
          )}
          <button
            onClick={() => reset()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              padding: '10px 20px',
              fontSize: 13,
              fontWeight: 700,
              color: '#fff',
              background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
              border: 'none',
              borderRadius: 12,
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(37, 99, 235, 0.25)',
            }}
          >
            Try again
          </button>
        </div>
      </body>
    </html>
  )
}
